
import { DarkDivHourGlassService } from "@cervval.js/client-html5-core/src/services/hourglass/darkDiv.hourglass.service";
import { DomTools } from "@cervval.js/client-html5-core/src/tools";
import { HashNavigationService } from "@cervval.js/client-html5-navigation/src/hash.navigation.service";
import { BasePageName } from "@cervval.js/client-html5-navigation/src/navigation.page";
import { NAVIGATION_SERVICE, NavigationService, NavigationServiceConfiguration } from "@cervval.js/client-html5-navigation/src/navigation.service";
import { DefaultToastService } from "@cervval.js/client-html5-ui/src/components/toast/toastService/toastService.impl/default.toast.service";
import { DefaultEditorFactoryService } from "@cervval.js/client-html5-ui/src/editors/defaultEditorFactory.service";
import { CjsBorderLayout } from "@cervval.js/client-html5-ui/src/layouts/borderLayout/borderLayout";
import { DragAndDropService } from "@cervval.js/client-html5-ui/src/services/dragAndDrop/dragAndDrop.service";
import { CLIENT_ROUTES_SERVICE, ClientRoutesService } from "@cervval.js/client-routes/src/clientRoutes.service";
import { XhrClientRoutesService } from "@cervval.js/client-routes/src/xhr.clientRoutes.service";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { DefaultAppService } from "@cervval.js/common/src/services/app.service";
import { DefaultDataSourceService } from "@cervval.js/common/src/services/dataSourceService.impl/defaultDataSourceService";
import { buildDefaultErrorMessageService } from "@cervval.js/common/src/services/errorMessage.impl/default.errorMessage.service";
import { NativeConsoleLogService } from "@cervval.js/common/src/services/log.impl/nativeConsole.log.service";
import { buildDefaultValidationService } from "@cervval.js/common/src/services/validation.impl/default.validation.service";
import { ObjectTools } from "@cervval.js/common/tools";
import "./spa.dom.scss"; // register global style (*.dom.scss files are injected in html by style-loader)
import { applyTheme, DefaultCjsThemeObjectTyping, DefaultCjsThemeVariable, defaultThemes, ThemeConfiguration, ThemeObject } from "./themes/theme.declarations";

ObjectTools.ensureLoaded(CjsBorderLayout);// ensure BorderLayout is loaded so we can use cjs-border-layout tag in html

/**
 * Register default services in dependency injection registry (injector)
 */
export function registerDefaultServices(): void {
    const options = { ignoreIfDependencyRegistered: true };// Do not register the dependency if the service was already registered with another instance
    injector.register(new NativeConsoleLogService(), options);// LogService
    injector.register(new DefaultAppService(), options);// AppService
    injector.register(new DarkDivHourGlassService(), options);// HourGlassService
    injector.register(new HashNavigationService(), options);// NavigationService
    injector.register(new XhrClientRoutesService(), options);// ClientRoutesService
    // injector.register(new FetchClientRoutesService(), options);// ClientRoutesService (This is another implementation of the ClientRouteService)
    injector.register(new DefaultDataSourceService(), options);// DefaultDataSourceService
    injector.register(buildDefaultErrorMessageService(), options);// ErrorMessageService
    injector.register(buildDefaultValidationService(), options);// ValidationService
    injector.register(new DefaultEditorFactoryService(), options);
    injector.register(new DefaultToastService(), options);// Register toast service
    injector.register(new DragAndDropService(), options);// Register drag and drop service
}

/**
 * @returns the service retrieved from injector
 */
export function getService<Service>(serviceName: string): Service {
    return injector.get<Service>(serviceName);
}

/**
 * @returns the client routes service retrieved from injector
 */
export function getClientRouteService(): ClientRoutesService {
    return injector.get<ClientRoutesService>(CLIENT_ROUTES_SERVICE);
}

/**
 * Configure navigation service
 */
export function configureNavigation<PageName extends BasePageName, PermissionContext = void>(configureNavigation: NavigationServiceConfiguration<PageName, PermissionContext>): void {
    const navigationService = injector.get<NavigationService<PageName, PermissionContext>>(NAVIGATION_SERVICE);
    navigationService.configure(configureNavigation);
}

/** Set bootstrap theme from localStorage or query string */
export function configureTheme<Theme extends DefaultCjsThemeObjectTyping, ThemeVariables extends DefaultCjsThemeVariable>(themes: ThemeConfiguration<Theme, ThemeVariables>): void {
    const THEME_VAR = "theme";
    // load theme from local storage
    let themeUid = localStorage?.getItem(THEME_VAR);
    let theme: ThemeObject<Theme, ThemeVariables> | undefined = undefined;

    // if set in current page query params, it overrides the theme (we can't get queryParams from NavigationService since it is not ready yet)
    const urlParams = new URLSearchParams(window.location.search);
    const queryTheme = urlParams.get(THEME_VAR);
    if (queryTheme) { themeUid = queryTheme; }
    if (themeUid) { theme = themes[themeUid]; }
    if (theme) { applyTheme(theme); }
}

/**
 * Prevents browser from showing default context menu
 * context menu is still enabled for (ctrl + shift + right_click)
 */
export function disableDefaultContextMenu(): void {
    document.addEventListener("contextmenu", event => {
        // context menu is still enabled for (ctrl + shift + right_click)
        if (event.shiftKey && event.ctrlKey) {
            return true;
        } else {
            event.preventDefault();
            return false;
        }
    });
}

/**
 * Configure default Single Page Application settings and returns the promise once DOM is loaded and ready for manipulation
 */
export function configure<PageName extends BasePageName, PermissionContext = void>(options?: {
    registerDefaultServices?: boolean,// default true
    configureNavigation?: NavigationServiceConfiguration<PageName, PermissionContext>,
    disableDefaultContextMenu?: boolean,
    themes?: ThemeConfiguration
}): Promise<void> {

    if (options?.registerDefaultServices !== false) {
        registerDefaultServices();
    }

    // configure theme
    configureTheme(options?.themes ?? defaultThemes);

    // optional context menu disabling on whole spa
    if (options?.disableDefaultContextMenu) {
        disableDefaultContextMenu();
    }

    return new Promise((resolve, reject) => {
        DomTools.onReady(() => {
            try {
                if (options?.configureNavigation) {
                    configureNavigation(options.configureNavigation);
                }
                resolve();
            } catch (err) {
                reject(err);
            }
        });
    });
}